type TurnstileAction = "sign_up" | "sign_in" | "magic_link" | "password_reset";

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  execute(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let siteKeyPromise: Promise<string | null> | null = null;
let scriptPromise: Promise<void> | null = null;

export async function getTurnstileToken(action: TurnstileAction): Promise<string | null> {
  const siteKey = await getSiteKey();
  if (!siteKey) return null;
  await loadScript();
  const turnstile = window.turnstile;
  if (!turnstile) return null;

  return new Promise((resolve) => {
    const container = document.createElement("div");
    container.hidden = true;
    document.body.appendChild(container);
    let settled = false;
    let widgetId = "";
    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      if (widgetId) turnstile.remove(widgetId);
      container.remove();
      resolve(token);
    };
    const timeout = window.setTimeout(() => finish(null), 15_000);
    widgetId = turnstile.render(container, {
      sitekey: siteKey,
      action,
      size: "invisible",
      execution: "execute",
      callback: (token: string) => {
        window.clearTimeout(timeout);
        finish(token);
      },
      "error-callback": () => {
        window.clearTimeout(timeout);
        finish(null);
      },
      "expired-callback": () => finish(null),
    });
    turnstile.execute(widgetId);
  });
}

function getSiteKey(): Promise<string | null> {
  siteKeyPromise ??= fetch("/api/portal/config", { credentials: "same-origin" })
    .then(async (response) => {
      if (!response.ok) return null;
      const value: unknown = await response.json();
      if (!value || typeof value !== "object" || !("turnstileSiteKey" in value)) return null;
      return typeof value.turnstileSiteKey === "string" ? value.turnstileSiteKey : null;
    })
    .catch(() => null);
  return siteKeyPromise;
}

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  scriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile failed to load")), { once: true });
    document.head.appendChild(script);
  });
  return scriptPromise;
}
